import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { getDownloadURL, ref, Storage, uploadBytes, uploadBytesResumable } from '@angular/fire/storage';
import { from, Observable, switchMap } from 'rxjs';
import { StoredFile } from '../../global/models';
import { FileDbService } from './file-db.service';
import { UploadOptions, UploadResult } from './storage.models';

/**
 * Generic service for uploading files to Firebase Storage and optionally
 * persisting the resulting StoredFile metadata in Firestore.
 *
 * @example
 * // Upload only, no Firestore save
 * this.storageService.uploadFile(file, 'files')
 *
 * @example
 * // Upload + save in Firestore with auto-generated ID, spread at root
 * this.storageService.uploadFile(file, `incidences/${id}/files`, `incidences/${id}/files`)
 *
 * @example
 * // Upload + save under a 'file' property, with custom doc ID and extra data
 * this.storageService.uploadFile(file, `orders/${id}/files`, `orders/${id}/files`, {
 *   docId: id,
 *   fileField: 'file',
 *   fileName: `${id}.pdf`,
 *   extraData: { status: 'pending' },
 * })
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private storage = inject(Storage);
  private fileDb = inject(FileDbService);
  private injector = inject(EnvironmentInjector);

  /**
   * Executes a function within an Angular injection context.
   * Prevents the warning: "Calling Firebase APIs outside of an Injection context
   * may destabilize your application leading to subtle change-detection and hydration bugs."
   */
  // TODO: quitar este bloque parche una vez que Firebase arregle el error "inject() may only be used during injection" al usar httpsCallableData fuera de un contexto de inyección (como en un servicio)
  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  /**
   * Uploads a file to Firebase Storage and optionally saves its metadata in Firestore.
   *
   * @param file - The File object to upload.
   * @param storagePath - Firebase Storage directory path (e.g. `incidences/${id}/files`).
   * @param firestorePath - Firestore collection path. If omitted, no Firestore doc is saved.
   * @param options - Additional upload options.
   * @param options.fileName - Custom filename for storage (used as-is). Defaults to the original `file.name`.
   * @param options.docId - Custom Firestore document ID. Defaults to auto-generated.
   * @param options.fileField - Property name to nest the StoredFile under.
   *   If omitted, the StoredFile is spread at root level.
   * @param options.extraData - Extra fields to merge into the Firestore document.
   * @returns Observable that emits the UploadResult once the operation completes.
   */
  uploadFile(
    file: File,
    storagePath: string,
    firestorePath?: string,
    options: UploadOptions = {},
  ): Observable<UploadResult> {
    const { fileName, docId, fileField, extraData } = options;
    const resolvedName = fileName ?? file.name;
    const filePath = `${storagePath}/${resolvedName}`;
    const storageRef = ref(this.storage, filePath);

    return from(this.run(() => uploadBytes(storageRef, file))).pipe(
      switchMap(async (snapshot) => {
        const url = await this.run(() => getDownloadURL(snapshot.ref));
        const storedFile: StoredFile = {
          name: file.name,
          contentType: file.type,
          path: filePath,
          size: file.size,
          url,
        };

        if (!firestorePath) {
          return { storedFile };
        }

        const fileData = fileField
          ? { [fileField]: storedFile }
          : { ...storedFile as unknown as Record<string, unknown> };
        const docData: Record<string, unknown> = { ...fileData, ...extraData };
        const firestoreId = await this.fileDb.save(docData, firestorePath, docId);
        return { storedFile, firestoreId };
      })
    );
  }



  /**
   * Uploads a file to Firebase Storage with real-time progress tracking,
   * and optionally saves its metadata in Firestore upon completion.
   *
   * @param file - The File object to upload.
   * @param storagePath - Firebase Storage directory path.
   * @param firestorePath - Firestore collection path. If omitted, no Firestore doc is saved.
   * @param options - Additional upload options.
   * @returns Observable emitting progress (0–100) during upload, and the final result on completion.
   */
  uploadFileWithProgress(
    file: File,
    storagePath: string,
    firestorePath?: string,
    options: UploadOptions = {},
  ): Observable<UploadProgressEvent> {
    const { fileName, docId, fileField, extraData } = options;
    const resolvedName = fileName ?? file.name;
    const filePath = `${storagePath}/${resolvedName}`;
    const storageRef = ref(this.storage, filePath);

    return new Observable<UploadProgressEvent>(subscriber => {
      const task = this.run(() => uploadBytesResumable(storageRef, file));

      task.on('state_changed',
        snapshot => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          subscriber.next({ type: 'progress', progress });
        },
        error => subscriber.error(error),
        async () => {
          try {
            const url = await this.run(() => getDownloadURL(task.snapshot.ref));
            const storedFile: StoredFile = {
              name: file.name,
              contentType: file.type,
              path: filePath,
              size: file.size,
              url,
            };

            let firestoreId: string | undefined;
            if (firestorePath) {
              const fileData = fileField
                ? { [fileField]: storedFile }
                : { ...storedFile as unknown as Record<string, unknown> };
              const docData: Record<string, unknown> = { ...fileData, ...extraData };
              firestoreId = await this.fileDb.save(docData, firestorePath, docId);
            }

            subscriber.next({ type: 'complete', storedFile, firestoreId });
            subscriber.complete();
          } catch (err) {
            subscriber.error(err);
          }
        },
      );
    });
  }
}

export type UploadProgressEvent =
  | { type: 'progress'; progress: number; }
  | { type: 'complete'; storedFile: StoredFile; firestoreId?: string; };
